"use client"

import { useCallback, useEffect, useMemo, useState } from "react"

import {
  filterDisplayTerms,
  loadDisplayTerms,
} from "@/lib/recon/drug-dictionary"

export function useDrugDictionary(
  query: string,
  excludeLower: ReadonlySet<string>,
  enabled = true,
): {
  terms: string[]
  suggestions: string[]
  loading: boolean
  error: string | null
} {
  const [terms, setTerms] = useState<string[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!enabled) return
    let cancelled = false
    setLoading(true)
    setError(null)

    void loadDisplayTerms()
      .then((list) => {
        if (!cancelled) {
          setTerms(list)
          setLoading(false)
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setTerms([])
          setError(
            err instanceof Error ? err.message : "Could not load drug dictionary",
          )
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [enabled])

  const suggest = useCallback(
    (q: string, existingLower: ReadonlySet<string>) => {
      const hits = filterDisplayTerms(terms, q)
      return hits.filter((t) => !existingLower.has(t.trim().toLowerCase()))
    },
    [terms],
  )

  const rxnormSuggestions = useMemo(() => {
    if (!enabled || !query.trim()) return []
    return suggest(query, excludeLower)
  }, [enabled, excludeLower, query, suggest])

  /**
   * Fallback for INVESTIGATIONAL assets.
   *
   * The list above is RxNav displaynames.json — 28,084 terms, every one an
   * APPROVED product, because RxNorm normalises drugs in the US supply chain.
   * Nothing pre-approval is in it, which is the wrong coverage boundary for a
   * competitive-intelligence tool: fosmanogepix (Basilea, Phase 3, 4 trials in
   * our own index) autocompletes to nothing while zanubrutinib and tirzepatide
   * resolve, purely because those are marketed.
   *
   * Deliberately a FALLBACK, not a union. Our index holds 246,226 distinct
   * primary_drug strings against RxNorm's 28,084, thick with dose and route
   * variants ("Fosmanogepix IV infusion"), so merging them would drown a clean
   * vocabulary. This fires only when RxNorm returns nothing, and the endpoint
   * ranks by trial count so the canonical name beats its own variants.
   */
  const [fallback, setFallback] = useState<string[]>([])
  useEffect(() => {
    const q = query.trim()
    // Only when RxNorm came back empty, and only once the query is long enough
    // to be a name rather than a keystroke (the endpoint refuses under 3 too).
    if (!enabled || q.length < 3 || rxnormSuggestions.length > 0) {
      setFallback([])
      return
    }
    const ac = new AbortController()
    const timer = setTimeout(() => {
      void (async () => {
        try {
          const res = await fetch(
            `/api/public/drug-name-suggestions?q=${encodeURIComponent(q)}`,
            { headers: { Accept: "application/json" }, signal: ac.signal },
          )
          if (!res.ok) return
          const json = (await res.json()) as {
            suggestions?: Array<{ name: string }>
          }
          if (ac.signal.aborted) return
          setFallback((json.suggestions ?? []).map((s) => s.name))
        } catch {
          // Additive: a failed fallback leaves the RxNorm behaviour intact.
          if (!ac.signal.aborted) setFallback([])
        }
      })()
    }, 250)
    return () => {
      clearTimeout(timer)
      ac.abort()
    }
  }, [enabled, query, rxnormSuggestions.length])

  const suggestions = useMemo(() => {
    if (rxnormSuggestions.length > 0) return rxnormSuggestions
    return fallback.filter((t) => !excludeLower.has(t.trim().toLowerCase()))
  }, [rxnormSuggestions, fallback, excludeLower])

  return { terms, suggestions, loading, error }
}
